import { courses, type Course } from '@/lib/courses'

export type WeekRange = { start: number; end: number }

export type OutlineLesson = {
  key: string
  title: string
  summary?: string
  moduleIndex: number
  preview: boolean
}

export type OutlineModule = {
  index: number
  title: string
  weeks: WeekRange
  lessons: OutlineLesson[]
}

export type CourseOutline = {
  slug: string
  title: string
  totalWeeks: number
  classOpening?: 'teacher'
  modules: OutlineModule[]
}

const WEEK_RANGE_PREFIX = /^weeks?\s+(\d+)(?:\s*[–—-]\s*(\d+))?\s*:/i
const LESSON_KEY = /^\d{2}-\d{2}$/

export function lessonKey(moduleIndex: number, lessonIndex: number) {
  return `${String(moduleIndex + 1).padStart(2, '0')}-${String(lessonIndex + 1).padStart(2, '0')}`
}

export function isLessonKey(value: string) {
  return LESSON_KEY.test(value)
}

function totalWeeks(course: Course) {
  const weeks = Number.parseInt(course.duration, 10)
  return Number.isFinite(weeks) && weeks > 0 ? weeks : course.curriculum.length
}

function moduleWeeks(course: Course, moduleIndex: number): WeekRange {
  const match = course.curriculum[moduleIndex].title.match(WEEK_RANGE_PREFIX)
  if (match) {
    const start = Number(match[1])
    return { start, end: Math.max(start, Number(match[2] ?? match[1])) }
  }
  const weeks = totalWeeks(course)
  const count = course.curriculum.length
  const start = Math.floor((moduleIndex * weeks) / count) + 1
  return { start, end: Math.max(start, Math.floor(((moduleIndex + 1) * weeks) / count)) }
}

export function getCourseOutline(courseSlug: string): CourseOutline | null {
  const course = courses.find((candidate) => candidate.slug === courseSlug)
  if (!course) return null
  return {
    slug: course.slug,
    title: course.title,
    totalWeeks: totalWeeks(course),
    ...(course.classOpening ? { classOpening: course.classOpening } : {}),
    modules: course.curriculum.map((module, moduleIndex) => ({
      index: moduleIndex,
      title: module.title,
      weeks: moduleWeeks(course, moduleIndex),
      lessons: module.lessons.map((lesson, lessonIndex) => ({
        key: lessonKey(moduleIndex, lessonIndex),
        title: lesson.title,
        ...(lesson.summary ? { summary: lesson.summary } : {}),
        moduleIndex,
        preview: lesson.preview === true,
      })),
    })),
  }
}

export function allLessons(outline: CourseOutline) {
  return outline.modules.flatMap((module) => module.lessons)
}

export function previewLessons(outline: CourseOutline) {
  return allLessons(outline).filter((lesson) => lesson.preview)
}

export function findLesson(outline: CourseOutline, key: string) {
  return allLessons(outline).find((lesson) => lesson.key === key)
}

export function modulesForWeek(outline: CourseOutline, week: number) {
  if (week < 1) return outline.modules.slice(0, 1)
  const current = outline.modules.filter(
    (module) => module.weeks.start <= week && week <= module.weeks.end,
  )
  return current.length > 0 ? current : outline.modules.slice(-1)
}

export function lessonsDueBy(outline: CourseOutline, week: number) {
  return outline.modules
    .filter((module) => module.weeks.start <= week)
    .flatMap((module) => module.lessons)
}

export function courseTitle(courseSlug: string) {
  return courses.find((course) => course.slug === courseSlug)?.title ?? courseSlug
}

export function courseOptions() {
  return courses.map((course) => ({
    slug: course.slug,
    title: course.title,
    status: course.status,
  }))
}
